/**
 * OperatorEmptyState Component
 *
 * Centered empty state with icon circle, title, description, and optional CTA.
 * CTA renders as <Link> (when ctaHref) or <button> (when onCtaClick).
 */

import { Link } from 'react-router-dom';
import { cn } from '../../../lib/utils';

interface OperatorEmptyStateProps {
  /** Lucide icon component to display in the circle */
  icon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  /** Main heading text */
  title: string;
  /** Supporting description text */
  description: string;
  /** Optional CTA button label */
  ctaLabel?: string;
  /** Renders CTA as <Link> when provided */
  ctaHref?: string;
  /** Renders CTA as <button> when provided (ctaHref takes precedence) */
  onCtaClick?: () => void;
  /** Additional CSS classes for the wrapper */
  className?: string;
}

export function OperatorEmptyState({
  icon: Icon,
  title,
  description,
  ctaLabel,
  ctaHref,
  onCtaClick,
  className,
}: OperatorEmptyStateProps) {
  const ctaClasses = cn(
    'mt-6 inline-flex w-full max-w-[280px] items-center justify-center',
    'min-h-[48px] rounded-xl bg-guild-green px-6 py-3',
    'text-sm font-semibold text-white',
    'active:scale-[0.98] active:bg-guild-green/90 transition-all duration-150',
    'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
    'disabled:opacity-50 disabled:pointer-events-none',
  );

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-16 px-8 text-center',
        className,
      )}
    >
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
        <Icon size={32} strokeWidth={1.5} className="text-gray-400" />
      </div>

      <h3 className="mt-4 text-lg font-semibold text-op-text-primary">{title}</h3>

      <p className="mt-2 max-w-[260px] text-sm text-op-text-secondary">{description}</p>

      {ctaLabel && ctaHref && (
        <Link to={ctaHref} className={ctaClasses}>
          {ctaLabel}
        </Link>
      )}

      {ctaLabel && !ctaHref && onCtaClick && (
        <button type="button" onClick={onCtaClick} className={ctaClasses}>
          {ctaLabel}
        </button>
      )}
    </div>
  );
}
